import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

const ThemeContext = createContext(null);
const STORAGE_KEY = "gmpulse-theme";

const systemDark = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

// Theme applies only to the internal app — never the public embeddable lead form.
const isPublicForm = (pathname) => pathname.startsWith("/audit-form");

export function ThemeProvider({ children }) {
  const location = useLocation();
  const [theme, setThemeState] = useState(() => localStorage.getItem(STORAGE_KEY) || "system");

  const resolved = theme === "system" ? (systemDark() ? "dark" : "light") : theme;

  // Apply/remove the `.dark` class on <html>
  useEffect(() => {
    const root = document.documentElement;
    const dark = !isPublicForm(location.pathname) &&
      (theme === "system" ? systemDark() : theme === "dark");
    root.classList.toggle("dark", dark);
  }, [theme, location.pathname]);

  // Follow OS changes while in "system" mode
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const dark = !isPublicForm(window.location.pathname) && mq.matches;
      document.documentElement.classList.toggle("dark", dark);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = useCallback((t) => {
    localStorage.setItem(STORAGE_KEY, t);
    setThemeState(t);
  }, []);

  const toggle = useCallback(() => {
    setTheme((theme === "system" ? (systemDark() ? "dark" : "light") : theme) === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext) || { theme: "light", resolved: "light", setTheme: () => {}, toggle: () => {} };
}
